import React, { useEffect, useState } from 'react';
import api from '../../api/axiosConfig';
import * as XLSX from 'xlsx';
import { AddressDisplay } from '../../utils/addressFormatter';
import { FileText, User, Car, Edit2, Trash2, Download } from 'lucide-react';

const Acente_Taleplerim = ({ user }) => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTransfers, setTotalTransfers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20; // Her sayfada 20 transfer
  
  // Responsive: Ekran boyutunu takip et
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      console.log('📋 Transferler yükleniyor...', {
        url: '/api/transfers/',
        user: user,
        baseURL: api.defaults.baseURL
      });
      
      // BÜTÜN transferleri getir (size parametresini çok büyük yapıyoruz)
      const response = await api.get('/api/transfers/', {
        params: {
          size: 10000,  // Çok büyük bir sayı - bütün transferleri getir
          page: 1
        }
      });
      
      const data = response.data.transfers || response.data || [];
      const total = response.data.total || data.length;
      const filteredData = data.filter((t) => t.status !== 'CANCELLED');
      
      // EN YENİ TARİHLER EN ÖNDE (azalan tarih sırası - DESC)
      const sortedData = filteredData.sort((a, b) => {
        const dateA = new Date(`${a.pickup_date}T${a.pickup_time}`);
        const dateB = new Date(`${b.pickup_date}T${b.pickup_time}`);
        return dateB - dateA; // En yeni en üstte
      });
      
      // Masaüstü pagination bilgilerini güncelle
      setTotalTransfers(sortedData.length);
      setTotalPages(Math.ceil(sortedData.length / pageSize));
      
      console.log('✅ Transferler yüklendi ve sıralandı:', {
        total: total,
        filtered: filteredData.length,
        sorted: sortedData.length,
        totalPages: Math.ceil(sortedData.length / pageSize)
      });
      
      setTransfers(sortedData);
    } catch (error) {
      console.error('❌ Transferler yüklenemedi:', {
        error: error.response?.data || error.message,
        status: error.response?.status,
        url: error.config?.url
      });
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.agency_id) {
      loadTransfers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.agency_id]); // Sadece user değiştiğinde yükle - pagination client-side

  const formatDateTime = (date, time) => {
    if (!date || !time) return '-';
    try {
      const dateObj = new Date(`${date}T${time}`);
      return dateObj.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return '-';
    try {
      const dateObj = new Date(dateTimeStr);
      return dateObj.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getServiceTypeLabel = (type) => {
    const types = {
      'PATIENT': 'Hasta',
      'STAFF': 'Personel', 
      'EXECUTIVE': 'Yönetici',
      'DOCUMENT': 'Evrak',
      'AIRPORT': 'Havalimanı Transfer',
      'MATERIAL': 'Malzeme'
    };
    return types[type] || type;
  };

  const getStatusLabel = (status) => {
    const statuses = {
      'RECEIVED': 'Talep Alındı',
      'PLANNED': 'Planlandı',
      'ENROUTE': 'Yola Çıktı',
      'AT_PICKUP': 'Konumda',
      'PICKED_UP': 'Yolcu Alındı',
      'COMPLETED': 'Tamamlandı'
    };
    return statuses[status] || status;
  };

  const StatusBadge = ({ status }) => {
    const getStatusColor = (status) => {
      const colors = {
        'RECEIVED': 'bg-yellow-100 text-yellow-800',
        'PLANNED': 'bg-blue-100 text-blue-800',
        'ENROUTE': 'bg-orange-100 text-orange-800',
        'AT_PICKUP': 'bg-purple-100 text-purple-800',
        'PICKED_UP': 'bg-indigo-100 text-indigo-800',
        'COMPLETED': 'bg-green-100 text-green-800'
      };
      return colors[status] || 'bg-gray-100 text-gray-800';
    };
    
    const className = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`;
    
    return <span className={className}>{getStatusLabel(status)}</span>;
  };

  const handleDeleteTransfer = async (transferId) => {
    if (!window.confirm('Bu transfer talebini silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      await api.delete(`/api/transfers/${transferId}`);
      alert('Transfer başarıyla silindi!');
      loadTransfers(); // Listeyi yenile
    } catch (error) {
      console.error('Transfer silinemedi:', error);
      alert('Transfer silinemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditTransfer = (transfer) => {
    setEditingTransfer(transfer);
    setShowEditModal(true);
  };

  const exportToExcel = () => {
    if (!transfers.length) return;
    
    const excelData = transfers.map((transfer, index) => ({
      'Sıra No': index + 1,
      'Alış Adresi': transfer.pickup_addr || '',
      'Bırakış Adresi': transfer.dropoff_addr || '',
      'Tarih-Saat': formatDateTime(transfer.pickup_date, transfer.pickup_time),
      'Yolcu Sayısı': transfer.pax_count || 1,
      'Yolcu Adı': transfer.passenger_name || '-',
      'TC/Pasaport/Evrak No': transfer.passenger_id_no || '-',
      'Sorumlu Kişi': transfer.responsible_person || '-',
      'Sorumlu İletişim': transfer.responsible_contact || '-',
      'Uçuş Kodu': transfer.flight_code || '-',
      'Hizmet Tipi': getServiceTypeLabel(transfer.service_type),
      'Notlar': transfer.notes || '-',
      'Durum': getStatusLabel(transfer.status),
      'Varış Saati': formatTime(transfer.at_pickup_time),
      'Alınan Saat': formatTime(transfer.picked_up_time),
      'Bırakılan Saat': formatTime(transfer.completed_time),
      'Şoför': transfer.driver_name || '-',
      'Oluşturulma': new Date(transfer.created_at).toLocaleDateString('tr-TR')
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Taleplerim');
    
    const fileName = `Taleplerim_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Mobil/Tablet görünümü için render
  if (isMobile) {
    return (
      <MobileRequestsView
        transfers={transfers}
        loading={loading}
        user={user}
        onEdit={handleEditTransfer}
        onDelete={handleDeleteTransfer}
        exportToExcel={exportToExcel}
        formatDateTime={formatDateTime}
        formatTime={formatTime}
        getServiceTypeLabel={getServiceTypeLabel}
        getStatusLabel={getStatusLabel}
        showEditModal={showEditModal}
        editingTransfer={editingTransfer}
        setShowEditModal={setShowEditModal}
        setEditingTransfer={setEditingTransfer}
        loadTransfers={loadTransfers}
      />
    );
  }

  // Desktop görünümü (mevcut kod)
  return (
    <div className="space-y-6">
      {/* Üst başlık ve eylemler */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Taleplerim</h2>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">
            Son güncelleme: {new Date().toLocaleTimeString('tr-TR')}
          </span>
          {transfers.length > 0 && (
            <button 
              onClick={exportToExcel} 
              className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Excel'e Aktar
            </button>
          )}
        </div>
      </div>

      {/* Tablo */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="w-full">
          <table className="w-full table-auto divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                  Alış
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                  Bırakış
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Tarih-Saat
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Yolcu
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Yolcu Adı
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  TC/Pasaport/Evrak No
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Sorumlu Kişi
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Sorumlu İletişim
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Uçuş Kodu
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Hizmet Tipi
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Durum
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Varış
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Alınan
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Bırakılan
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Şoför
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Telefon
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  Araç Plakası
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                  Notlar
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28 sticky right-0 bg-gray-50">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan={20} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                      <span className="ml-2 text-gray-600">Yükleniyor...</span>
                    </div>
                  </td>
                </tr>
              )}
              
              {!loading && transfers.length === 0 && (
                <tr>
                  <td colSpan={20} className="px-6 py-12 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="font-medium">Henüz transfer talebi bulunmuyor</p>
                    <p className="text-sm">Yeni talep oluşturmak için "Talep Oluştur" sayfasını kullanın</p>
                  </td>
                </tr>
              )}
              
              {!loading && transfers.map((transfer) => (
                <tr key={transfer.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <AddressDisplay address={transfer.pickup_addr} />
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <AddressDisplay address={transfer.dropoff_addr} />
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatDateTime(transfer.pickup_date, transfer.pickup_time)}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                    {transfer.pax_count || 1}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <div className="break-words" title={transfer.passenger_name}>
                      {transfer.passenger_name || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <div className="break-words" title={transfer.passenger_id_no}>
                      {transfer.passenger_id_no || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <div className="break-words" title={transfer.responsible_person}>
                      {transfer.responsible_person || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <div className="break-words" title={transfer.responsible_contact}>
                      {transfer.responsible_contact || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <div className="break-words font-mono" title={transfer.flight_code}>
                      {transfer.flight_code || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {getServiceTypeLabel(transfer.service_type)}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm">
                    <StatusBadge status={transfer.status} />
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700 font-mono">
                    {formatTime(transfer.at_pickup_time)}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700 font-mono">
                    {formatTime(transfer.picked_up_time)}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700 font-mono">
                    {formatTime(transfer.completed_time)}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <div className="break-words" title={transfer.driver_name}>
                      {transfer.driver_name || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <div className="break-words" title={transfer.driver_phone}>
                      {transfer.driver_phone || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900">
                    <div className="break-words font-mono" title={transfer.vehicle_no}>
                      {transfer.vehicle_no || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 max-w-xs">
                    <div className="break-words text-xs leading-relaxed" title={transfer.notes}>
                      {transfer.notes || '-'}
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-center sticky right-0 bg-white">
                    <div className="flex items-center justify-center space-x-3">
                      {/* Düzenleme butonu - sadece RECEIVED durumunda */}
                      {transfer.status === 'RECEIVED' && (
                        <button
                          onClick={() => handleEditTransfer(transfer)}
                          className="text-blue-600 hover:text-blue-900 transition-colors p-1 hover:bg-blue-50 rounded"
                          title="Düzenle"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                      
                      {/* Silme butonu - her statuste */}
                      <button
                        onClick={() => handleDeleteTransfer(transfer.id)}
                        className="text-red-600 hover:text-red-900 transition-colors p-1 hover:bg-red-50 rounded"
                        title="Sil"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow-sm mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            {/* Mobil pagination */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Önceki
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Toplam <span className="font-medium">{totalTransfers}</span> transfer,{' '}
                <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> - <span className="font-medium">{Math.min(currentPage * pageSize, totalTransfers)}</span> arası gösteriliyor
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">İlk sayfa</span>
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Önceki</span>
                  «
                </button>
                
                {/* Sayfa numaraları */}
                {[...Array(totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  // Sadece birkaç sayfa göster (1, 2, 3, ..., son)
                  if (
                    pageNum === 1 || 
                    pageNum === totalPages || 
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-red-50 border-red-500 text-red-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === currentPage - 2 || 
                    pageNum === currentPage + 2
                  ) {
                    return <span key={pageNum} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                  }
                  return null;
                })}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Sonraki</span>
                  »
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Son sayfa</span>
                  »»
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Düzenleme Modal'ı */}
      {showEditModal && editingTransfer && (
        <TransferEditModal 
          transfer={editingTransfer}
          onClose={() => {
            setShowEditModal(false);
            setEditingTransfer(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setEditingTransfer(null);
            loadTransfers();
          }}
        />
      )}

    </div>
  );
};

// Transfer Düzenleme Modal Component'i (Admin'deki ile aynı)
const TransferEditModal = ({ transfer, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    pickup_addr: transfer.pickup_addr || '',
    dropoff_addr: transfer.dropoff_addr || '',
    pickup_date: transfer.pickup_date || '',
    pickup_time: transfer.pickup_time || '',
    pax_count: transfer.pax_count || 1,
    passenger_name: transfer.passenger_name || '',
    passenger_id_no: transfer.passenger_id_no || '',
    service_type: transfer.service_type || '',
    notes: transfer.notes || '',
    responsible_person: transfer.responsible_person || '',
    responsible_contact: transfer.responsible_contact || '',
    flight_code: transfer.flight_code || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        service_type: formData.service_type || null
      };

      const response = await api.put(`/api/transfers/${transfer.id}`, submitData);
      
      if (response.status === 200) {
        alert('Transfer başarıyla güncellendi!');
        onSuccess && onSuccess();
      }
    } catch (error) {
      console.error('Transfer güncellenemedi:', error);
      setError(error.response?.data?.detail || 'Transfer güncellenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleOverlayClick}>
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">Transfer Düzenle #{transfer.id}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Alış Adresi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alış Adresi *
                </label>
                <input
                  type="text"
                  name="pickup_addr"
                  value={formData.pickup_addr}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Bırakış Adresi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bırakış Adresi *
                </label>
                <input
                  type="text"
                  name="dropoff_addr"
                  value={formData.dropoff_addr}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Alış Tarihi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alış Tarihi *
                </label>
                <input
                  type="date"
                  name="pickup_date"
                  value={formData.pickup_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Alış Saati */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alış Saati *
                </label>
                <input
                  type="time"
                  name="pickup_time"
                  value={formData.pickup_time}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Yolcu Sayısı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yolcu Sayısı *
                </label>
                <input
                  type="number"
                  name="pax_count"
                  value={formData.pax_count}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Hizmet Tipi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hizmet Tipi
                </label>
                <select
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seçiniz</option>
                  <option value="PATIENT">Hasta</option>
                  <option value="STAFF">Personel</option>
                  <option value="EXECUTIVE">Yönetici</option>
                  <option value="DOCUMENT">Evrak</option>
                  <option value="AIRPORT">Havalimanı Transfer</option>
                  <option value="MATERIAL">Malzeme</option>
                </select>
              </div>

              {/* Yolcu Adı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yolcu Adı
                </label>
                <input
                  type="text"
                  name="passenger_name"
                  value={formData.passenger_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Yolcu adı giriniz"
                />
              </div>

              {/* Yolcu TC/Pasaport/Evrak No */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TC/Pasaport/Evrak No
                </label>
                <input
                  type="text"
                  name="passenger_id_no"
                  value={formData.passenger_id_no}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="TC No, Pasaport No veya Evrak No"
                />
              </div>

              {/* Sorumlu Kişi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sorumlu Kişi
                </label>
                <input
                  type="text"
                  name="responsible_person"
                  value={formData.responsible_person}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Sorumlu kişi adı"
                />
              </div>

              {/* Sorumlu İletişim */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sorumlu İletişim
                </label>
                <input
                  type="text"
                  name="responsible_contact"
                  value={formData.responsible_contact}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Telefon numarası"
                />
              </div>

              {/* Uçuş Kodu */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Uçuş Kodu
                </label>
                <input
                  type="text"
                  name="flight_code"
                  value={formData.flight_code}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Örn: TK123"
                />
              </div>

              {/* Notlar */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notlar
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Eklemek istediğiniz notlar..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Güncelleniyor...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Güncelle</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MOBİL/TABLET GÖRÜNÜMÜ
// ============================================

const MobileRequestsView = ({
  transfers,
  loading,
  user,
  onEdit,
  onDelete,
  exportToExcel,
  formatDateTime,
  formatTime,
  getServiceTypeLabel,
  getStatusLabel,
  showEditModal,
  editingTransfer,
  setShowEditModal,
  setEditingTransfer,
  loadTransfers
}) => {
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [activeTab, setActiveTab] = useState('genel');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // Mobilde sayfa başına 10 transfer
  
  // Sayfalama için transferleri böl
  const totalPages = Math.ceil(transfers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTransfers = transfers.slice(startIndex, endIndex);

  const tabs = [
    { id: 'genel', label: 'Genel', icon: FileText },
    { id: 'yolcu', label: 'Yolcu', icon: User },
    { id: 'arac', label: 'Şoför & Araç', icon: Car }
  ];

  const toggleRequest = (id) => {
    if (expandedRequest === id) {
      setExpandedRequest(null);
    } else {
      setExpandedRequest(id);
      setActiveTab('genel'); // Yeni kart açıldığında ilk tab'a dön
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'RECEIVED': 'bg-yellow-100 text-yellow-800',
      'PLANNED': 'bg-blue-100 text-blue-800',
      'ENROUTE': 'bg-orange-100 text-orange-800',
      'AT_PICKUP': 'bg-purple-100 text-purple-800',
      'PICKED_UP': 'bg-indigo-100 text-indigo-800',
      'COMPLETED': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const renderTabContent = (transfer) => {
    switch(activeTab) {
      case 'genel':
        return (
          <div className="space-y-3 p-4 bg-gray-50">
            <InfoRow label="Alış" value={transfer.pickup_addr} />
            <InfoRow label="Bırakış" value={transfer.dropoff_addr} />
            <InfoRow label="Tarih-Saat" value={formatDateTime(transfer.pickup_date, transfer.pickup_time)} />
            <InfoRow label="Hizmet Tipi" value={getServiceTypeLabel(transfer.service_type)} badge="blue" />
            <InfoRow label="Durum" value={getStatusLabel(transfer.status)} badgeClass={getStatusColor(transfer.status)} />
            {transfer.notes && transfer.notes !== '-' && (
              <div className="pt-2 border-t border-gray-200">
                <div className="text-sm text-gray-600 mb-2">Notlar</div>
                <div className="text-sm text-gray-900 bg-yellow-50 p-3 rounded border border-yellow-200">
                  {transfer.notes}
                </div>
              </div>
            )}
          </div>
        );
      case 'yolcu':
        return (
          <div className="space-y-3 p-4 bg-gray-50">
            <InfoRow label="Yolcu Sayısı" value={transfer.pax_count || 1} />
            <InfoRow label="Yolcu Adı" value={transfer.passenger_name || '-'} />
            <InfoRow label="TC/Pasaport/Evrak No" value={transfer.passenger_id_no || '-'} />
            <InfoRow label="Sorumlu Kişi" value={transfer.responsible_person || '-'} />
            <InfoRow label="Sorumlu İletişim" value={transfer.responsible_contact || '-'} />
            <InfoRow label="Uçuş Kodu" value={transfer.flight_code || '-'} />
          </div>
        );
      case 'arac':
        return (
          <div className="p-4 bg-gray-50">
            {/* Şoför Bilgisi */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-start justify-between mb-2">
                <span className="text-sm text-gray-600">Şoför</span>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{transfer.driver_name || '-'}</div>
                  <div className="text-sm text-gray-600 mt-0.5">{transfer.driver_phone || '-'}</div>
                </div>
              </div>
            </div>

            {/* Araç Plakası */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <InfoRow label="Araç Plakası" value={transfer.vehicle_no || '-'} />
            </div>

            {/* Saat Bilgileri - Timeline */}
            {(transfer.at_pickup_time || transfer.picked_up_time || transfer.completed_time) && (
              <div>
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Saat Bilgileri
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    {/* Şoför Varış */}
                    <div className="flex-1 text-center">
                      <div className="text-xs text-gray-500 mb-1">Şoför Varış</div>
                      <div className="text-base font-bold text-red-600">{formatTime(transfer.at_pickup_time)}</div>
                    </div>
                    
                    {/* Arrow */}
                    <div className="text-gray-300">→</div>
                    
                    {/* Alınan Saat */}
                    <div className="flex-1 text-center">
                      <div className="text-xs text-gray-500 mb-1">Alınan Saat</div>
                      <div className="text-base font-bold text-green-600">{formatTime(transfer.picked_up_time)}</div>
                    </div>
                    
                    {/* Arrow */}
                    <div className="text-gray-300">→</div>
                    
                    {/* Bırakılan Saat */}
                    <div className="flex-1 text-center">
                      <div className="text-xs text-gray-500 mb-1">Bırakılan Saat</div>
                      <div className="text-base font-bold text-blue-600">{formatTime(transfer.completed_time)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Taleplerim</h1>
              <p className="text-xs text-gray-500">BHT Transfer Sistemi</p>
            </div>
          </div>
          {transfers.length > 0 && (
            <button 
              onClick={exportToExcel}
              className="bg-green-600 text-white px-3 py-2 rounded text-xs font-medium flex items-center gap-1"
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
          )}
        </div>
      </div>

      {/* Title and Last Update */}
      <div className="px-4 py-4 border-b border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500">Son güncelleme: {new Date().toLocaleTimeString('tr-TR')}</p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
          <span className="ml-2 text-gray-600">Yükleniyor...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && transfers.length === 0 && (
        <div className="px-4 py-12 text-center text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="font-medium">Henüz transfer talebi bulunmuyor</p>
          <p className="text-sm">Yeni talep oluşturmak için "Talep Oluştur" sayfasını kullanın</p>
        </div>
      )}

      {/* Request Cards */}
      {!loading && paginatedTransfers.map((transfer) => (
        <div key={transfer.id} className="px-4 pb-4">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {/* Request Header */}
            <div 
              className="p-4 cursor-pointer active:bg-gray-50"
              onClick={() => toggleRequest(transfer.id)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {/* Tarih Saat ve Durum */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-600">
                      {formatDateTime(transfer.pickup_date, transfer.pickup_time)}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getStatusColor(transfer.status)}`}>
                      {getStatusLabel(transfer.status)}
                    </span>
                  </div>
                  
                  {/* Alış → Bırakış (ALT ALTA) */}
                  <div className="space-y-1">
                    <div className="flex items-start gap-2 text-gray-700 text-sm">
                      <span className="text-green-600 font-semibold flex-shrink-0">Alış:</span>
                      <span className="font-medium break-words">{transfer.pickup_addr}</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-700 text-sm">
                      <span className="text-red-600 font-semibold flex-shrink-0">Bırakış:</span>
                      <span className="font-medium break-words">{transfer.dropoff_addr}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 ml-2">
                  {transfer.status === 'RECEIVED' && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(transfer);
                      }}
                      className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(transfer.id);
                    }}
                    className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {expandedRequest === transfer.id && (
              <div className="border-t border-gray-200">
                {/* Tabs */}
                <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 min-w-[90px] px-3 py-3 text-xs font-medium transition-colors ${
                          activeTab === tab.id
                            ? 'text-red-600 border-b-2 border-red-600 bg-white'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Icon className="w-4 h-4 mx-auto mb-1" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* Tab Content */}
                {renderTabContent(transfer)}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Mobil Pagination */}
      {!loading && totalPages > 1 && (
        <div className="px-4 py-4 border-t border-gray-200 bg-white sticky bottom-0">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              « Önceki
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                Sayfa {currentPage} / {totalPages}
              </span>
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            >
              Sonraki »
            </button>
          </div>
          
          {/* Sayfa bilgisi */}
          <div className="text-center mt-2">
            <p className="text-xs text-gray-500">
              Toplam {transfers.length} transfer, {startIndex + 1}-{Math.min(endIndex, transfers.length)} arası gösteriliyor
            </p>
          </div>
        </div>
      )}

      {/* Transfer Düzenleme Modal'ı */}
      {showEditModal && editingTransfer && (
        <TransferEditModal 
          transfer={editingTransfer}
          onClose={() => {
            setShowEditModal(false);
            setEditingTransfer(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setEditingTransfer(null);
            loadTransfers();
          }}
        />
      )}
    </div>
  );
};

// Yardımcı Component: InfoRow
function InfoRow({ label, value, badge, badgeClass, highlight }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      {badge || badgeClass ? (
        <span className={`px-2 py-1 text-xs rounded-full font-medium ${badgeClass || 'bg-blue-100 text-blue-800'}`}>
          {value}
        </span>
      ) : highlight ? (
        <span className="text-sm font-semibold text-red-600">{value === '-' ? '-' : value}</span>
      ) : (
        <span className="text-sm font-medium text-gray-900 text-right">{value === '-' ? '-' : value}</span>
      )}
    </div>
  );
}

export default Acente_Taleplerim;
